import React from "react";
import { defaultFooterContent } from "./defaultFooterContent";
import { Link, FooterContent } from "@/types/footer";
import { LinkWrapper } from "@/components/LinkWrapper";
import { socialIcons } from "@/lib/socialIcons";
import { trackEvent } from "@/components/analytics";
import { BillingContactDetails } from "@/components/BillingContactDetails";

// Extract the anonymous function into a shared function
function groupLinksByColumn(columns: Link[]): Record<string, Link[]> {
    return columns.reduce((acc: Record<string, Link[]>, column: Link) => {
        if (!column.label || !column.url) return acc; // Skip null entries
        const columnName = column.columnName || 'General'; // Default to 'General'
        if (!acc[columnName]) acc[columnName] = [];
        acc[columnName].push({ label: column.label, url: column.url });
        return acc;
    }, {});
}

export default function ModernCleanFooter({
    content = defaultFooterContent,
}: { content?: FooterContent }) {
    return (
        <footer className="bg-background py-4">
            <div className="container mx-auto px-4 py-4">
                <div className="mb-4 flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
                    {content.logo && (
                        <a href="/" className="flex items-center" aria-label="Logo - go to homepage">
                            <img
                                src={content.logo.src}
                                alt={content.logo.alt}
                                loading="lazy"
                                className="h-12 w-auto object-contain text-primary"
                            />
                        </a>
                    )}
                </div>
                <div className="mb-4 grid gap-4 border-y border-border/25 py-4 md:grid-cols-3">
                    {Object.entries(groupLinksByColumn(content.columns)).map(([columnName, links], index) => (
                        <div key={index}>
                            <h3 className="mb-2 font-semibold text-foreground">
                                {columnName}
                            </h3>
                            <ul className="space-y-1">
                                {links.map((link: Link, linkIndex: number) => (
                                    <li key={linkIndex}>
                                        <LinkWrapper
                                            to={link.url}
                                            className="text-foreground transition-colors hover:text-primary hover:underline"
                                        >
                                            {link.label}
                                        </LinkWrapper>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                    {content.contact && (
                        <div className="mt-4 text-left text-foreground">
                            {content.contact.phone && (
                                <a
                                    href={`tel:${content.contact.phone}`}
                                    className="block text-foreground transition-colors hover:text-primary hover:underline"
                                    onClick={() => trackEvent("phone_click", { phone: content.contact.phone, location: "footer" })}
                                >
                                    Phone: {content.contact.phone}
                                </a>
                            )}
                           {content.contact.fax && <p className="text-foreground">Fax: {content.contact.fax}</p>}
                            {content.contact.address && <p>Address: {content.contact.address}</p>}
                            {content.contact.officeHours && (
                                <div className="flex items-start gap-2">
                                    <span className="shrink-0">Office Hours:</span>
                                    <span className="whitespace-pre-line">{content.contact.officeHours}</span>
                                </div>
                            )}
                            {content.contact.bookingLink && (
                                <LinkWrapper
                                    to={content.contact.bookingLink}
                                    className="mt-2 block text-foreground transition-colors hover:text-primary hover:underline"
                                    onClick={() => trackEvent("booking_link_click", { location: "footer" })}
                                >
                                    Book appointment today
                                </LinkWrapper>
                            )}
                            <BillingContactDetails
                                phone={content.contact.billingPhone}
                                email={content.contact.billingEmail}
                                location="footer"
                                className="mt-2"
                                linkClassName="text-foreground transition-colors hover:text-primary hover:underline"
                            />
                        </div>
                    )}
                </div>
                <div className="flex flex-col items-center justify-between gap-2 md:flex-row md:items-center">
                    <div className="text-foreground">
                        {content.copyright}
                    </div>

                    <div className="flex gap-2">
                        {content.legalLinks.map((link: Link, index: number) => (
                            <a
                                key={index}
                                href={link.url}
                                target={link.opensInNewTab ? "_blank" : undefined}
                                rel={link.opensInNewTab ? "noopener noreferrer" : undefined}
                                className="text-foreground transition-colors hover:text-primary hover:underline"
                            >
                                {link.label}
                                {link.opensInNewTab && <span className="sr-only"> (opens in a new tab)</span>}
                            </a>
                        ))}
                    </div>
                    {content.socialLinks?.length > 0 && (
                        <div className="flex items-center gap-2">
                            {content.socialLinks.map((social: Link) => (
                                <a
                                    key={social.label}
                                    href={social.url}
                                    className="text-foreground transition-colors hover:text-primary hover:underline"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`Visit our ${social.label} page (opens in a new tab)`}
                                    onClick={() => trackEvent("social_click", { platform: social.label, location: "footer" })}
                                >
                                    {socialIcons[
                                        social.label as keyof typeof socialIcons
                                    ] &&
                                        React.createElement(
                                            socialIcons[
                                            social.label as keyof typeof socialIcons
                                            ],
                                            { className: "h-5 w-5", "aria-hidden": true },
                                        )}
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </footer>
    );
}
